import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  llmPreferenceApi,
  llmProvidersApi,
  userSecretsApi,
  LLMPreference,
  ProviderId,
  ProviderInfo,
  ProviderSecretStatus,
} from '../api/userSecrets';
import { formatApiError } from '../utils/errorUtils';

export type AiProviderPickerMode = 'profile' | 'register';

/**
 * Always-present (partial) draft. Empty strings mean "not yet filled".
 * Use isAiDraftConfigured() to check whether the draft is complete enough to submit.
 */
export type AiProviderDraft = {
  provider: ProviderId | '';
  model: string;
  api_key: string;
};

export const EMPTY_AI_DRAFT: AiProviderDraft = { provider: '', model: '', api_key: '' };

export const isAiDraftConfigured = (d: AiProviderDraft | null | undefined): boolean =>
  !!d && !!d.provider && !!d.model && d.api_key.trim().length >= 10;

interface AiProviderPickerProps {
  mode: AiProviderPickerMode;
  /** Register-only: controlled value (parent owns the data so it survives step navigation). */
  value?: AiProviderDraft;
  /** Register-only: called whenever any field changes (raw values, may be partial). */
  onChange?: (next: AiProviderDraft) => void;
}

/** Profile-mode imperative handle exposed via ref so a parent (e.g. ProfileModal) can trigger save. */
export interface AiProviderPickerHandle {
  /** Persist any pending changes (typed key and/or changed provider+model). Returns true on success. */
  saveAll: () => Promise<boolean>;
  /** True if there are any unsaved local changes the user would expect to be persisted on Save. */
  hasUnsavedChanges: () => boolean;
}

const helpForProvider = (id: ProviderId | '', available: ProviderInfo[]): string => {
  if (!id) return '';
  const p = available.find((x) => x.id === id);
  return p?.api_key_help || '';
};

const EyeIcon: React.FC<{ off?: boolean }> = ({ off }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
    {off && <path d="M3 3l18 18" />}
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1.2 13.2A2 2 0 0 1 15.8 21H8.2a2 2 0 0 1-2-1.8L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const AiProviderPicker = forwardRef<AiProviderPickerHandle, AiProviderPickerProps>(({ mode, value, onChange }, ref) => {
  const isRegister = mode === 'register';
  const controlled = isRegister && !!value && !!onChange;

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [available, setAvailable] = useState<ProviderInfo[]>([]);
  const [statusList, setStatusList] = useState<ProviderSecretStatus[]>([]);

  // Internal state is the source of truth in profile mode; in register mode the parent's `value`
  // is authoritative and this is unused.
  const [internalProvider, setInternalProvider] = useState<ProviderId | ''>('');
  const [internalModel, setInternalModel] = useState<string>('');
  const [internalApiKey, setInternalApiKey] = useState<string>('');
  const [showKey, setShowKey] = useState(false);
  const [keyFocused, setKeyFocused] = useState(false);

  // Profile mode: remember the values loaded from the server so we know what is actually "dirty".
  const [loadedProvider, setLoadedProvider] = useState<ProviderId | ''>('');
  const [loadedModel, setLoadedModel] = useState<string>('');

  const provider: ProviderId | '' = controlled ? value!.provider : internalProvider;
  const model: string = controlled ? value!.model : internalModel;
  const apiKey: string = controlled ? value!.api_key : internalApiKey;

  const setProvider = (next: ProviderId | '') => {
    if (controlled) {
      // Switching provider clears the api_key (it belongs to the previous provider).
      const apiKeyNext = next !== value!.provider ? '' : value!.api_key;
      onChange!({ provider: next, model: value!.model, api_key: apiKeyNext });
    } else {
      if (next !== internalProvider) setInternalApiKey('');
      setInternalProvider(next);
    }
  };

  const setModel = (next: string) => {
    if (controlled) {
      onChange!({ provider: value!.provider, model: next, api_key: value!.api_key });
    } else {
      setInternalModel(next);
    }
  };

  const setApiKey = (next: string) => {
    if (controlled) {
      onChange!({ provider: value!.provider, model: value!.model, api_key: next });
    } else {
      setInternalApiKey(next);
    }
  };

  const status = useMemo<ProviderSecretStatus | undefined>(
    () => statusList.find((s) => s.provider === provider),
    [statusList, provider]
  );

  const currentProviderInfo = useMemo<ProviderInfo | undefined>(
    () => available.find((p) => p.id === provider),
    [available, provider]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isRegister) {
          // Anonymous endpoint: pulls the curated registry from the backend so the
          // registration UI stays in sync with the server's truth (no hardcoded list).
          const providers = await llmProvidersApi.list();
          if (cancelled) return;
          setAvailable(providers);
          setStatusList([]);
          // Preselect first provider + its default model so the model dropdown is populated
          // on first render. Only seed defaults if the parent hasn't already provided values.
          if (providers.length > 0 && controlled && !value!.provider && !value!.model) {
            onChange!({
              provider: providers[0].id,
              model: providers[0].default_model,
              api_key: value!.api_key,
            });
          }
          setLoading(false);
          return;
        }
        const pref: LLMPreference = await llmPreferenceApi.get();
        if (cancelled) return;
        setAvailable(pref.available_providers || []);
        setStatusList(pref.status_per_provider || []);
        if (pref.provider) {
          setInternalProvider(pref.provider);
          setInternalModel(pref.model || '');
          setLoadedProvider(pref.provider);
          setLoadedModel(pref.model || '');
        } else if ((pref.available_providers || []).length > 0) {
          // Pre-select the first option so the user immediately sees model choices.
          // Mirror the same values into "loaded" so the auto-selection doesn't show up as dirty.
          const first = pref.available_providers[0];
          setInternalProvider(first.id);
          setInternalModel(first.default_model);
          setLoadedProvider(first.id);
          setLoadedModel(first.default_model);
        }
      } catch (e: any) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[AiProviderPicker] failed to load providers', e);
        setError(formatApiError(e?.response?.data?.detail) || e?.message || 'Nu am putut încărca lista de furnizori AI.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegister]);

  // Whenever provider changes, ensure model belongs to that provider's curated list.
  useEffect(() => {
    if (!provider) return;
    const p = available.find((x) => x.id === provider);
    if (!p) return;
    if (!p.models.includes(model)) {
      setModel(p.default_model);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, available]);

  const reloadStatus = async (): Promise<ProviderSecretStatus[]> => {
    try {
      const list = await userSecretsApi.list();
      setStatusList(list);
      return list;
    } catch {
      // best-effort
      return statusList;
    }
  };

  const onDeleteKey = async () => {
    if (!provider) return;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      await userSecretsApi.deleteKey(provider);
      await reloadStatus();
      setInfo('Cheia a fost ștearsă.');
    } catch (e: any) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || 'Nu am putut șterge cheia.');
    } finally {
      setBusy(false);
    }
  };

  const hasUnsavedChanges = (): boolean => {
    if (isRegister) return false;
    if (apiKey.trim().length > 0) return true;
    if (provider !== loadedProvider) return true;
    if (model !== loadedModel) return true;
    return false;
  };

  const saveAll = async (): Promise<boolean> => {
    if (isRegister) return true; // register mode persists via parent flow
    if (!hasUnsavedChanges()) return true;
    if (!provider || !model) return true;
    setBusy(true);
    setError('');
    setInfo('');
    try {
      const typedKey = apiKey.trim();
      if (typedKey.length > 0 && typedKey.length < 10) {
        setError('Cheia API pare prea scurtă.');
        return false;
      }

      let freshStatusList = statusList;
      if (typedKey.length >= 10) {
        await userSecretsApi.setKey(provider, typedKey);
        freshStatusList = await reloadStatus();
        setApiKey('');
      }

      const requiresKey = !!currentProviderInfo?.requires_api_key;
      if (requiresKey) {
        const freshStatus = freshStatusList.find((s) => s.provider === provider);
        if (!freshStatus?.configured) {
          setError('Introdu cheia API pentru furnizorul ales înainte de a salva preferința.');
          return false;
        }
      }

      const prefChanged = provider !== loadedProvider || model !== loadedModel;
      if (prefChanged) {
        await llmPreferenceApi.set(provider, model);
        setLoadedProvider(provider);
        setLoadedModel(model);
      }

      setInfo('Setările AI au fost salvate.');
      return true;
    } catch (e: any) {
      setError(formatApiError(e?.response?.data?.detail) || e?.message || 'Nu am putut salva setările AI.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      saveAll,
      hasUnsavedChanges,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [provider, model, apiKey, loadedProvider, loadedModel, statusList, currentProviderInfo, isRegister]
  );

  if (loading) {
    return <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>Se încarcă opțiunile AI…</div>;
  }

  if (!available.length) {
    return (
      <div>
        {error && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-3)' }}>
            {error}
          </div>
        )}
        <div className="muted" style={{ fontSize: 'var(--text-sm)' }}>
          {error
            ? 'Lista de furnizori AI nu poate fi încărcată. Încearcă din nou mai târziu.'
            : 'Nu există furnizori AI disponibili.'}
        </div>
      </div>
    );
  }

  const help = provider ? helpForProvider(provider, available) : '';

  return (
    <div>
      {error && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-3)' }}>
          {error}
        </div>
      )}
      {info && !error && (
        <div className="muted" style={{ marginBottom: 'var(--sp-3)', fontSize: 'var(--text-xs)' }}>
          {info}
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Furnizor AI</label>
        <select
          className="form-input"
          value={provider}
          onChange={(e) => {
            const next = e.target.value as ProviderId;
            if (next !== provider) {
              setError('');
              setInfo('');
            }
            setProvider(next);
          }}
          disabled={busy}
        >
          {available.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Model</label>
        <select
          className="form-input"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          disabled={busy || !currentProviderInfo}
        >
          {(currentProviderInfo?.models || []).map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      {currentProviderInfo?.requires_api_key && (
        <div className="form-group">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 6,
            }}
          >
            <label className="form-label" style={{ margin: 0 }}>
              Cheie API ({currentProviderInfo.label})
            </label>
            {!isRegister && (
              <span
                className={`badge badge-dot ${status?.configured ? 'badge-green' : 'badge-slate'}`}
                title={
                  status?.configured
                    ? `Cheie configurată${status.last4 ? ` (ultimele 4: ${status.last4})` : ''}`
                    : 'Nicio cheie configurată'
                }
              >
                {status?.configured
                  ? `Configurat${status.last4 ? ` · …${status.last4}` : ''}`
                  : 'Neconfigurat'}
              </span>
            )}
          </div>

          {(() => {
            const isMaskShown =
              !isRegister &&
              !keyFocused &&
              apiKey === '' &&
              !!status?.configured;
            const maskedDisplay = `••••••••••••${status?.last4 ?? ''}`;
            const maskStyle = isMaskShown
              ? { fontFamily: 'monospace', letterSpacing: '0.05em', color: 'var(--color-slate-500)' }
              : {};
            return (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 0 }}>
                  <input
                    className="form-input"
                    type={isMaskShown || showKey ? 'text' : 'password'}
                    value={isMaskShown ? maskedDisplay : apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    onFocus={() => setKeyFocused(true)}
                    onBlur={() => setKeyFocused(false)}
                    placeholder={
                      isRegister || !status?.configured
                        ? 'Lipește aici cheia API'
                        : 'Lipește o cheie nouă pentru a o înlocui'
                    }
                    disabled={busy}
                    autoComplete="off"
                    spellCheck={false}
                    style={{ paddingRight: 40, ...maskStyle }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowKey((s) => !s)}
                    disabled={busy || isMaskShown}
                    style={{
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 32,
                      height: 32,
                      padding: 0,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                    title={
                      isMaskShown
                        ? 'Cheia salvată nu poate fi afișată'
                        : showKey
                          ? 'Ascunde cheia'
                          : 'Afișează cheia'
                    }
                    aria-label={showKey ? 'Ascunde cheia' : 'Afișează cheia'}
                  >
                    <EyeIcon off={showKey} />
                  </button>
                </div>
                {!isRegister && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={onDeleteKey}
                    disabled={busy || !status?.configured}
                    style={{ width: 38, height: 38, padding: 0, display: 'grid', placeItems: 'center' }}
                    title="Șterge cheia API"
                    aria-label="Șterge cheia API"
                  >
                    <TrashIcon />
                  </button>
                )}
              </div>
            );
          })()}

          {help && (
            <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 6 }}>
              {help}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

AiProviderPicker.displayName = 'AiProviderPicker';

export default AiProviderPicker;
